import { Injectable, inject, signal } from '@angular/core';
import { getStoredUser, setStoredUser } from '../helpers/user-storage.util';
import { UserService } from './user.service';

export type Theme = 'light' | 'dark';

/**
 * Owns the app's light/dark theme.
 *
 * Source of truth is the logged-in user's `darkModeEnabled` boolean (default
 * `false` -> LIGHT). At startup the theme is resolved from the stored user
 * (see {@link getStoredUser}); false/absent -> light, true -> dark. The resolved
 * theme is always stamped on `<html>` as `data-theme="light|dark"` so the token
 * layer / Material dark theme key off it explicitly (there is no OS-follow
 * fallback anymore — a plain boolean can't express "auto").
 *
 * On a change the flag is persisted to the user via `UserService.updateDarkMode`,
 * the stored user is updated so the choice survives navigation, and a
 * `localStorage` mirror (`connect-theme`) is kept ONLY as the pre-bootstrap
 * anti-flash cache read by the inline script in index.html. The DOM is updated
 * optimistically; the persist call does not block the UI, and a failed persist
 * keeps the optimistic change (it is only logged).
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  static readonly STORAGE_KEY = 'connect-theme';

  private readonly userService = inject(UserService);

  // Resolved theme currently applied — what the toggle reflects.
  private readonly _theme = signal<Theme>(this.resolveInitialTheme());
  readonly theme = this._theme.asReadonly();

  constructor() {
    this.applyResolvedTheme();
  }

  /** Current resolved theme. */
  get current(): Theme {
    return this._theme();
  }

  /** Apply a theme, stamp the DOM immediately, and persist the choice. */
  setTheme(theme: Theme): void {
    this._theme.set(theme);
    this.applyResolvedTheme();
    this.persist(theme === 'dark');
  }

  /** Flip the current theme and persist the result. */
  toggle(): void {
    this.setTheme(this._theme() === 'dark' ? 'light' : 'dark');
  }

  /**
   * Re-resolve + apply the theme from the currently stored user WITHOUT
   * persisting. The service is constructed at app boot (before a fresh login
   * populates the stored user), and login is an SPA navigation with no reload,
   * so the login success path calls this to reflect the just-fetched user's
   * `darkModeEnabled`. No persist — the value came from the server.
   */
  applyFromStoredUser(): void {
    const theme: Theme = getStoredUser()?.darkModeEnabled ? 'dark' : 'light';
    this._theme.set(theme);
    this.applyResolvedTheme();
  }

  /**
   * Resolves the theme at startup from the stored user's `darkModeEnabled`
   * flag (false/absent -> light). Does not touch the DOM (that's
   * applyResolvedTheme).
   */
  private resolveInitialTheme(): Theme {
    return getStoredUser()?.darkModeEnabled ? 'dark' : 'light';
  }

  /**
   * Stamps the resolved theme on `<html>` and mirrors it into the anti-flash
   * cache. Called by every apply path — including the constructor/boot — so the
   * cache is always kept in sync and the inline index.html script never reads a
   * stale value on a plain reload.
   */
  private applyResolvedTheme(): void {
    document.documentElement.setAttribute('data-theme', this._theme());
    this.writeCache(this._theme());
  }

  /**
   * Persists the preference: optimistically flips `darkModeEnabled` on the
   * stored user, then PUTs it. We deliberately do NOT merge the server's
   * full-user response back — the endpoint only flips this one flag, so the
   * optimistic single-field write already reflects what the server stored, and
   * re-spreading a request-time snapshot could clobber a field another flow
   * changed in the meantime. A failed persist keeps the optimistic change and
   * is only logged.
   */
  private persist(enabled: boolean): void {
    setStoredUser({ ...getStoredUser(), darkModeEnabled: enabled });
    this.userService.updateDarkMode(enabled).subscribe({
      error: (error) => {
        console.error('Failed to persist dark mode preference', error);
      },
    });
  }

  /**
   * Mirrors the resolved theme into localStorage as the pre-bootstrap anti-flash
   * cache (read by the inline script in index.html). Persistence failures are
   * ignored — the in-memory signal still applies for this session.
   */
  private writeCache(theme: Theme): void {
    try {
      localStorage.setItem(ThemeService.STORAGE_KEY, theme);
    } catch {
      // localStorage unavailable (e.g. privacy mode) — ignore.
    }
  }
}
